const debug = require('debug')('mtk:clients');

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowUpRight, FiServer, FiUser } from 'react-icons/fi';

import { Link } from 'react-router-dom';
import AppLink from '../components/common/Link';

import api from '../utils/api';
import { axios } from '../utils/api/utils';
import { useQuery } from '../utils/query';

import { routes } from '../routes';
import { can } from '../can';

import { Loading } from './PageStatus';
import PageHeader from '../components/common/PageHeader';
import { List, Header, Body, Row, Cell } from '../components/common/List';
import Button from '../components/common/Button';
import Pagination from '../components/common/Pagination';
import classnames from 'classnames';
import { modeLabels } from '../constants';
import { notificationTemplateCatalog } from '../utils/defaultNotificationTemplates';

import css from './Clients.css';

const clientPreferences = (client) => {
  const prefs = client && client.preferences;
  if (!prefs) return {};
  if (typeof prefs === 'string') {
    try {
      return JSON.parse(prefs);
    } catch (err) {
      return {};
    }
  }
  return prefs;
};

const assignedClientTemplates = (client) => {
  const prefs = clientPreferences(client);
  return Object.entries(prefs)
    .filter(([key, value]) => key.endsWith('Template') && value != null && value !== '' && typeof value !== 'object')
    .map(([key, value]) => {
      const type = key.slice(0, -'Template'.length);
      const entry = notificationTemplateCatalog.find((item) => item.type === type);
      return { key, id: value, label: (entry && entry.label) || key };
    })
    .sort((a, b) => String(a.label).localeCompare(String(b.label), 'es'));
};

const segmentDetails = {};
const segmentDetailRequests = {};

const fetchSegmentDetail = (id) => {
  if (segmentDetails[id]) return Promise.resolve(segmentDetails[id]);
  if (!segmentDetailRequests[id]) {
    segmentDetailRequests[id] = axios
      .get(`/segments/${id}`)
      .then((res) => {
        const data = res.data && res.data.data ? res.data.data : {};
        segmentDetails[id] = data;
        return data;
      })
      .finally(() => {
        delete segmentDetailRequests[id];
      });
  }
  return segmentDetailRequests[id];
};

const GroupLabel = ({ segment, match }) => {
  const isScope = match === 'scope';
  const anchorRef = useRef(null);
  const timerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(segmentDetails[segment.id] || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState(null);

  const placeTooltip = () => {
    const rect = anchorRef.current && anchorRef.current.getBoundingClientRect();
    if (!rect) return;
    setTooltipStyle({ top: rect.bottom, left: rect.left });
  };

  const onEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (segmentDetails[segment.id]) {
      setDetail(segmentDetails[segment.id]);
      setOpen(true);
      placeTooltip();
      return;
    }
    timerRef.current = setTimeout(() => {
      setOpen(true);
      setLoading(true);
      setError(null);
      placeTooltip();
      fetchSegmentDetail(segment.id)
        .then((data) => {
          setDetail(data);
        })
        .catch(() => {
          setError('No se pudo cargar el grupo');
        })
        .finally(() => {
          setLoading(false);
        });
    }, 2000);
  };

  const onLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(false);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const rawItems = isScope ? detail && detail.users : detail && detail.lockers;
  const items = Array.isArray(rawItems) ? rawItems : [];
  const heading = isScope ? 'Usuarios' : 'Lockers';
  const emptyLabel = isScope ? 'Sin usuarios asociados' : 'Sin lockers asociados';

  return (
    <span ref={anchorRef} className={css.groupHover} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <Link
        className={classnames(css.groupLabel, isScope ? css.groupScope : css.groupAssigned)}
        to={routes('segment', { id: segment.id })}>
        {isScope ? <FiUser aria-hidden="true" /> : <FiServer aria-hidden="true" />}
        {segment.name}
      </Link>
      {open ? (
        <div className={css.groupTooltip} style={tooltipStyle} role="tooltip">
          <strong>{heading}</strong>
          {loading && !detail ? <span>Cargando…</span> : null}
          {error ? <span>{error}</span> : null}
          {detail && !items.length ? <span>{emptyLabel}</span> : null}
          {items.length ? (
            <ul>
              {items.map((item) => {
                const itemId = item && typeof item === 'object' ? item.id : item;
                const itemName = item && typeof item === 'object' ? item.name || item.id : item;
                const to = itemId != null ? routes(isScope ? 'user' : 'locker', { id: itemId }) : null;
                return (
                  <li key={itemId || itemName} className={css.groupItem}>
                    <span>{itemName}</span>
                    {to ? (
                      <AppLink
                        to={to}
                        inline
                        target="_blank"
                        rel="noopener noreferrer"
                        className={css.groupItemLink}
                        tooltip={isScope ? `Ver usuario #${itemId}` : `Ver locker #${itemId}`}>
                        <FiArrowUpRight />
                      </AppLink>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </span>
  );
};

const Clients = (props) => {
  const query = useQuery(props.location, props.history);
  const { user } = api.auth();
  const { index, actions } = api.clients.index(query.obj);
  const { actions: segmentsActions } = api.segments.index(null, { lazy: true });
  const [segments, setSegments] = useState([]);
  const [segmentsLoaded, setSegmentsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    segmentsActions.getAll({ relations: 'clients,users' }).then((all) => {
      if (cancelled) return;
      setSegments(all && all.toJS ? all.toJS() : []);
      setSegmentsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const segmentsByClientId = useMemo(() => {
    const groups = {};
    const add = (clientId, segment, match) => {
      if (clientId == null || clientId === '') return;
      const key = String(clientId);
      if (!groups[key]) groups[key] = [];
      if (groups[key].some((item) => item.segment.id === segment.id)) return;
      groups[key].push({ segment, match });
    };

    segments.forEach((segment) => {
      if (segment.scopeToClientId != null) add(segment.scopeToClientId, segment, 'scope');

      const assignedClients = Array.isArray(segment.clients) ? segment.clients : [];
      assignedClients.forEach((assignedClient) => {
        const assignedClientId = assignedClient && typeof assignedClient === 'object' ? assignedClient.id : assignedClient;
        add(assignedClientId, segment, 'assigned');
      });
    });

    return groups;
  }, [segments]);

  return (
    <main>
      <PageHeader title="Clientes">
        {can(user, 'create', 'clients') && (
          <Button noMargin to={routes('newClient')}>
            Nuevo
          </Button>
        )}
      </PageHeader>

      {index.loaded ? (
        <List>
          <Header>
            <Cell head className={css.num}>#</Cell>
            <Cell head>ID</Cell>
            <Cell head>Nombre</Cell>
            <Cell head>Grupos</Cell>
            <Cell head>Preferencias</Cell>
            <Cell head>Plantillas</Cell>
            <Cell head>Modo</Cell>
            <Cell head>2FA</Cell>
            <Cell head>Códigos personalizados</Cell>
            <Cell head>Devolucion</Cell>
            <Cell head>Auto revocación</Cell>
          </Header>
          <Body>
            {index.data.toJS().map((client, i) => {
              const pageInfo = index.pageInfo && index.pageInfo.toJS ? index.pageInfo.toJS() : {};
              const perPage = Number(pageInfo.perPage) || index.data.size;
              const rowNumber = ((Number(pageInfo.curPage) || 1) - 1) * perPage + i + 1;
              const to = routes('client', { id: client.id });
              const prefs = clientPreferences(client);
              const assignedTemplates = assignedClientTemplates(client);
              const templatesTo = `${to}#templates`;
              const clientSegments = segmentsByClientId[String(client.id)] || [];
              const preferenceLabels = [
                prefs.enableProductsMenu && { key: 'products', label: 'Menú productos', tone: css.groupGreen },
                prefs.enableUsersNationalId && { key: 'rut', label: 'Campo RUT', tone: css.groupBlue },
                prefs.medical && { key: 'medical', label: 'Médico', tone: css.groupViolet },
              ].filter(Boolean);
              return (
                <Row key={`client-row-${i}`}>
                  <Cell className={css.num}>{rowNumber}</Cell>
                  <Cell>
                    <Link to={to}>{client.id}</Link>
                  </Cell>
                  <Cell>
                    <Link to={to}>{client.name}</Link>
                  </Cell>
                  <Cell className={css.groupsCell}>
                    <div className={css.groups}>
                      {clientSegments.map(({ segment, match }) => (
                        <GroupLabel key={`client-${client.id}-segment-${segment.id}`} segment={segment} match={match} />
                      ))}
                      {segmentsLoaded && !clientSegments.length ? (
                        <span className={css.noGroups}>Sin grupos</span>
                      ) : null}
                    </div>
                  </Cell>
                  <Cell className={css.groupsCell}>
                    <div className={css.groups}>
                      {preferenceLabels.map((preference) => (
                        <Link
                          key={`client-${client.id}-pref-${preference.key}`}
                          className={classnames(css.groupLabel, preference.tone)}
                          to={to}>
                          {preference.label}
                        </Link>
                      ))}
                      {!preferenceLabels.length ? <span className={css.noGroups}>Ninguna</span> : null}
                    </div>
                  </Cell>
                  <Cell className={css.groupsCell}>
                    <div className={css.groups}>
                      {assignedTemplates.map((template) => (
                        <Link
                          key={`client-${client.id}-template-${template.key}`}
                          className={classnames(css.groupLabel, css.groupAssigned)}
                          title={`${template.label} · ID ${template.id}`}
                          to={templatesTo}>
                          {template.label}
                          <span className={css.templateId}>ID {template.id}</span>
                        </Link>
                      ))}
                      {!assignedTemplates.length ? <span className={css.noGroups}>No hay plantillas</span> : null}
                    </div>
                  </Cell>
                  <Cell>
                    <Link to={to}>
                      {prefs.mode && prefs.mode == 'housing' ? (
                        <span className={classnames(css.optionType, [css['border-warning']])}>
                          {modeLabels('housing')}
                        </span>
                      ) : (
                        <span className={classnames(css.optionType, [css['border-enabled']])}>
                          {modeLabels('normal')}
                        </span>
                      )}
                    </Link>
                  </Cell>
                  <Cell>
                    <Link to={to}>
                      {prefs.twoFactorAuth ? (
                        <span className={classnames(css.optionType, css.enabled)}>Activado</span>
                      ) : (
                        <span className={classnames(css.optionType)}>Desactivado</span>
                      )}
                    </Link>
                  </Cell>

                  <Cell>
                    <Link to={to}>
                      {prefs.customDropCodeEnabled ? (
                        <span className={classnames(css.optionType, [css['enabled']])}>Drop</span>
                      ) : (
                        ''
                      )}

                      {prefs.reversalCodeEnabled ? (
                        <span className={classnames(css.optionType, [css['enabled']])}>Reversal</span>
                      ) : (
                        ''
                      )}

                      {prefs.courierPickupCodeEnabled ? (
                        <span className={classnames(css.optionType, [css['enabled']])}>Courier pickup</span>
                      ) : (
                        ''
                      )}
                      {!prefs.customDropCodeEnabled && !prefs.courierPickupCodeEnabled && !prefs.reversalCodeEnabled ? (
                        <span className={classnames(css.optionType)}>Desactivado</span>
                      ) : (
                        ''
                      )}
                    </Link>
                  </Cell>

                  <Cell>
                    <Link to={to}>
                      {prefs.returnsEnabled ? (
                        <span className={classnames(css.optionType, css.warning)}>Habilitada</span>
                      ) : (
                        <span className={classnames(css.optionType)}>Desactivada</span>
                      )}

                      {prefs.returnsEnabled && prefs.autoCancelReturnEnabled ? (
                        <div>
                          <span>
                            Auto-cancelación en <b>{prefs.autoCancelReturnHours}</b> hrs.
                          </span>
                        </div>
                      ) : (
                        ''
                      )}
                    </Link>
                  </Cell>
                  <Cell>
                    <Link to={to}>
                      {prefs.autoReversalEnabled ? (
                        <span className={classnames(css.optionType, [css['danger']])}>Habilitada</span>
                      ) : (
                        <span className={classnames(css.optionType)}>Desactivada</span>
                      )}

                      {prefs.autoReversalEnabled ? (
                        <div>
                          <span>
                            Auto-revocación en <b>{prefs.autoReversalHours}</b> hrs.
                          </span>
                        </div>
                      ) : (
                        ''
                      )}
                    </Link>
                  </Cell>
                </Row>
              );
            })}
          </Body>
        </List>
      ) : (
        <Loading compact />
      )}
      {index.pageInfo && <Pagination {...index.pageInfo.toJS()} onChange={query.setPage} />}
    </main>
  );
};

export default Clients;
